import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Select,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useCallback } from "react";
import variables from "../../../assets/styles/variables.module.scss";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { red } from "@mui/material/colors";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useState, } from "react";

import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { useDispatch } from "react-redux";
import {
  ShowError,
  ShowSuccess,
} from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import {
  AGENT,
  AGENT_SHORTCUT,
  DASHBOARD_CON_EXPORT_ADMIN,
  DASHBOARD_CON_HISTORY_ADMIN,
  DASHBOARD_CON_EXPORT,
  DASHBOARD_CON_HISTORY,
} from "../../../Services/Endpoint";
import { stat } from "fs";
import userContext from "../../../Shared-ui/Context/UserContext";
import { formatDate } from "../../../Shared-ui/utils/Common/Common";
import dayjs from "dayjs";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import ButtonGroup from "@mui/material/ButtonGroup";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FilterListIcon from "@mui/icons-material/FilterList";

// Debounce utility function
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const ConversationHistory = () => {
  const { user } = useContext(userContext);
  const dispatch = useDispatch();
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [timeFrom, setTimeFrom] = useState<Date | null>(null);
  const [timeTo, setTimeTo] = useState<Date | null>(null);
  const [conversations, setConversations] = useState<any[]>([]);
  const [filteredRows, setFilteredRows] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });
  const [totalMessages, setTotalMessages] = useState(0);
  const [search, setSearch] = useState("");
  const [searchByAgent, setSearchByAgent] = useState<any>(null);
  const [agentInputValue, setAgentInputValue] = useState("");
  const [agents, setAgents] = useState<any[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentInfo, setAgentInfo] = useState([]);
  const [openExport, setOpenExport] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedExport, setSelectedExport] = useState(0);

  const user_role = user?.role;

  const isUser = user_role === "admin" || user_role === "super admin";

  const endpoint = isUser ? DASHBOARD_CON_HISTORY_ADMIN : DASHBOARD_CON_HISTORY
  const exportEndpoint = isUser ? DASHBOARD_CON_EXPORT_ADMIN : DASHBOARD_CON_EXPORT
  const historyEndpoint = isUser ? DASHBOARD_CON_HISTORY_ADMIN : DASHBOARD_CON_HISTORY

  // Debounced agent search function
  const debouncedGetAgents = useCallback(
    debounce((searchTerm: string) => {
      getAgents(searchTerm);
    }, 500),
    []
  );

  useEffect(() => {
    if (!dateFrom && !dateTo && !searchByAgent && !search) {
      getConversationHistory();
    }
  }, [dateFrom, dateTo, searchByAgent, search]);

  // Load agents when user types (debounced server-side search)
  useEffect(() => {
    if (agentInputValue.length >= 2) {
      debouncedGetAgents(agentInputValue);
    } else {
      // Clear agents when input is less than 2 characters
      setAgents([]);
    }
  }, [agentInputValue, debouncedGetAgents]);

  useEffect(() => {
    getConversationHistory();
  }, [pagination]);

  useEffect(() => {
    if (conversations && search) {
      const data = conversations.filter(
        (row) =>
          row.question.toLowerCase().includes(search.toLowerCase()) ||
          row.response.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredRows(data);
      setTotalMessages(data.length);
    }
  }, [search]);

  const getAgents = async (searchTerm: string) => {
    try {
      setLoadingAgents(true);
      let url = `${AGENT}?page=1&page_size=100&filter=all&search=${encodeURIComponent(searchTerm)}`;
      const data = await BaseInstance.get(url).then((res) => res?.data);
      const agentList = data?.items || [];
      agentList.sort(
        (a: { created_at: string }, b: { created_at: string }) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setAgents(agentList);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText || err?.response?.data?.detail));
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const getConversationHistory = async () => {
    try {
      dispatch(StartLoading());
      var url = historyEndpoint;
      const params: string[] = [];

      if ((dateFrom && !dateTo) || (!dateFrom && dateTo)) {
        dispatch(ShowError("Please select both From Date and To Date"));
        dispatch(StopLoading());
        return;
      }

      if (dateFrom) {
        params.push(`start_date=${dayjs(dateFrom).format("YYYY-MM-DD")}`);
      }
      if (dateTo) {
        params.push(`end_date=${dayjs(dateTo).format("YYYY-MM-DD")}`);
      }
      if (searchByAgent?.id) {
        params.push(`agent_id=${searchByAgent.id}`);
      }

      if (params.length > 0) {
        url +=
          `?limit=${pagination.pageSize}&offset=${pagination.page}&` +
          params.join("&");
      } else {
        url += `?limit=${pagination.pageSize}&offset=${pagination.page}`;
      }

      const data = await BaseInstance.get(url).then((res) => res?.data);
      const conversations = data.conversations || [];
      conversations.sort(
        (a: { created_at: string }, b: { created_at: string }) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setTotalMessages(data.total_messages);
      setConversations(conversations);
      setFilteredRows(conversations);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const getConversationHistoryExport = async (format: string) => {
    try {
      dispatch(StartLoading());
      var url = `${exportEndpoint}?format=${format}`;
      if (dateFrom && dateTo) {
        url =
          url +
          `&start_date=${dayjs(dateFrom).format("YYYY-MM-DD")}&end_date=${dayjs(
            dateTo
          ).format("YYYY-MM-DD")}`;
      } else if (dateFrom) {
        url = url + `&start_date=${dayjs(dateFrom).format("YYYY-MM-DD")}`;
      } else if (dateTo) {
        url = url + `&end_date=${dayjs(dateTo).format("YYYY-MM-DD")}`;
      }
      const response = await BaseInstance.get(url, {
        responseType: "blob", // Important for file downloads!
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `conversation_history.${format}`); // Specify the file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const handlePaginationMetaChange = (paginationMeta: any) => {
    const { page, pageSize, rowCount } = paginationMeta;
    setPagination({ page: page + 1, pageSize: pageSize });
  };

  const clearFilters = () => {
    // this include manual refresh also
    setDateFrom(null);
    setDateTo(null);
    setSearchByAgent(null);
    setAgentInputValue("");
    setAgents([]); // Clear agents since we refetch on every search now
    setSearch("");
    getConversationHistory();
  };

  const columns: GridColDef[] = [
    //   { field: "id", headerName: "ID", width: 70 },
    {
      field: "created_at",
      headerName: "Date",
      width: 230,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Typography variant="body2">{formatDate(params.value)}</Typography>
          </Box>
        );
      },
    },
    {
      field: "user_email",
      headerName: "User",
      width: 150,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Typography variant="body2">{params.value || "N/A"}</Typography>
          </Box>
        );
      },
    },
    { field: "agent_name", headerName: "Agent", width: 200 },
    {
      field: "question",
      headerName: "Question",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const getQuestionInformation = () => {
          try {
            const questionData = params.value.split("\n");
            console.log(questionData);
            if (questionData.length > 0) {
              return questionData[0];
            } else {
              return "";
            }
          } catch (error) {
            console.log(error);
          }
        };
        return (
          <Tooltip
            title={
              <Box
                sx={{
                  maxWidth: 400,
                  maxHeight: 200,
                  overflow: "auto",
                  whiteSpace: "pre-line",
                  p: 1,
                }}
              >
                {/* {params.value} */}
                {getQuestionInformation()}
              </Box>
            }
            arrow
            placement="top"
            enterDelay={500}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 180,
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                {getQuestionInformation()}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "response",
      headerName: "Response",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip
          title={
            <Box
              sx={{
                maxWidth: 400,
                maxHeight: 200,
                overflow: "auto",
                whiteSpace: "pre-line",
                p: 1,
              }}
            >
              {params.value}
            </Box>
          }
          arrow
          placement="top"
          enterDelay={500}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 180,
                cursor: "pointer",
                width: "100%",
              }}
            >
              {params.value}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },

    { field: "tokens_input", headerName: "Tokens Input", width: 110 },
    { field: "tokens_output", headerName: "Tokens Output", width: 110 },
    { field: "tokens_total", headerName: "Tokens Total", width: 110 },
    { field: "provider", headerName: "Provider", width: 110 },
    { field: "model", headerName: "Model", width: 110 },
    { field: "time_seconds", headerName: "Time (ms)", width: 110 },
    { field: "", headerName: "Feedback", width: 110 },
  ];

  const handleExportToggle = () => {
    setOpenExport((prevOpen) => !prevOpen);
  };

  const handleExportMenuClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpenExport(false);
  };

  const handleExportMenuItemClick = async (index: number, format: string) => {
    setSelectedExport(index);
    setOpenExport(false);
    if (index === 0) {
      await getConversationHistoryExport(format);
    } else if (index === 1) {
      await getConversationHistoryExport(format);
    }
  };

  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "space-between", sm: "flex-end" },
          // mt: "-54px",
          mt: { xs: "0", sm: "-54px" },
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowFiltersMobile((prev) => !prev)}
          startIcon={<FilterListIcon />}
          sx={{
            display: { xs: "inline-flex", sm: "none" },
            textTransform: "none",
          }}
        >
          Filter
        </Button>
        <Button
          variant="outlined"
          startIcon={<CachedOutlinedIcon />}
          onClick={() => clearFilters()}
          sx={{
            // width: { xs: "100%", sm: "auto" },
            //borderColor: variables.primaryButton,
            //color: variables.primaryButton,
            textTransform: "capitalize",
            "&:hover": {
              borderColor: "primary.dark",
              color: "primary.dark",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Manual Refresh
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 2,
          flexDirection: {
            xs: "column",
            sm: "column",
            md: "column",
            lg: "row",
          },
        }}
      >
        {(showFiltersMobile || window.innerWidth >= 600) && (
          <Box
            sx={{
              border: "1px solid rgba(0, 0, 0, 0.12)",
              backdropFilter: "blur(5px)",
              backgroundColor: "rgba(256, 256, 256, 0.7)",
              borderRadius: "4px",
              p: 2,
              position: "relative",
              zIndex: 5,
              //display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              alignItems: {
                xs: "stretch",
                sm: "center",
              },
              gap: 2,
              flexWrap: "wrap",
              flex: 3,
              // Hide on mobile if not toggled
              display: {
                xs: showFiltersMobile ? "flex" : "none",
                sm: "flex",
              },
            }}
          >
            <FormControl
              fullWidth
              variant="outlined"
              size="small"
              sx={{ flex: { sm: 3, xs: "unset" }, minWidth: "160px" }}
            >
              <Autocomplete
                value={searchByAgent}
                onChange={(event, newValue) => {
                  setSearchByAgent(newValue);
                }}
                inputValue={agentInputValue}
                onInputChange={(event, newInputValue) => {
                  setAgentInputValue(newInputValue);
                }}
                options={agents}
                loading={loadingAgents}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                filterOptions={(options, state) => {
                  // ensure suggestions shown to the user are filtered by agent name only.
                  const input = (state.inputValue || "").toLowerCase();
                  if (!input) return options;
                  return options.filter((opt: any) =>
                    (opt?.name || "").toLowerCase().includes(input)
                  );
                }}
                noOptionsText={
                  agentInputValue.length < 2
                    ? "Type at least 2 characters to search"
                    : loadingAgents
                    ? "Loading agents..."
                    : "No agents found"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Agent"
                    placeholder="Type to search agents..."
                    InputLabelProps={{
                      shrink: true,
                      sx: { background: "#fff", px: 0.5 }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAgents ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                  },
                }}
              />
            </FormControl>

            {/* From Date */}
            <FormControl
              variant="outlined"
              size="small"
              sx={{ width: { xs: "100%", sm: "120px" } }}
            >
              <InputLabel
                shrink
                htmlFor="dateFrom"
                sx={{
                  background: "#fff",
                  px: 0.5,
                  transform: "translate(14px, -6px) scale(0.75)",
                }}
              >
                From Date
              </InputLabel>
              <DatePicker
                selected={dateFrom}
                onChange={(date) => setDateFrom(date)}
                placeholderText="dd/mm/yyyy"
                dateFormat="dd/MM/yyyy"
                maxDate={dateTo && dateTo < new Date() ? dateTo : new Date()}
                customInput={
                  <TextField
                    variant="outlined"
                    size="small"
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": { height: "40px" },
                    }}
                    inputProps={{ maxLength: 50, style: { height: "20px" } }}
                  />
                }
              />
            </FormControl>

            {/* To Date */}
            <FormControl
              variant="outlined"
              size="small"
              sx={{ width: { xs: "100%", sm: "120px" } }}
            >
              <InputLabel
                shrink
                htmlFor="dateTo"
                sx={{
                  background: "#fff",
                  px: 0.5,
                  transform: "translate(14px, -6px) scale(0.75)",
                }}
              >
                To Date
              </InputLabel>
              <DatePicker
                selected={dateTo}
                onChange={(date) => setDateTo(date)}
                placeholderText="dd/mm/yyyy"
                dateFormat="dd/MM/yyyy"
                minDate={dateFrom ?? undefined}
                maxDate={new Date()}
                customInput={
                  <TextField
                    variant="outlined"
                    size="small"
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": { height: "40px" },
                    }}
                    inputProps={{ maxLength: 50, style: { height: "20px" } }}
                  />
                }
              />
            </FormControl>

            {/* Search Button */}
            <Button
              variant="contained"
              disableElevation
              size="medium"
              onClick={getConversationHistory}
              sx={{
                height: "38px",
                minWidth: "100px",
                textTransform: "capitalize",
                borderColor: variables.primaryButton,
                color: variables.primaryButton,
                "&:hover": {
                  borderColor: "primary.dark",
                  color: "primary.dark",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Search
            </Button>

            {/* Clear Button */}
            <Button
              variant="outlined"
              size="medium"
              onClick={clearFilters}
              sx={{
                height: "38px",
                minWidth: "100px",
                textTransform: "capitalize",
              }}
            >
              Clear
            </Button>
          </Box>
        )}

        <Box
          sx={{
            border: "1px solid rgba(0, 0, 0, 0.12)",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(256, 256, 256, 0.7)",
            borderRadius: "4px",
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flex: 1,
            position: "relative",
            zIndex: 5,
          }}
        >
          <TextField
            placeholder="Search Conversation"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: { xs: "100%", sm: "100%", md: "100%", lg: "260px" },
              "& .MuiOutlinedInput-root": {
                height: "36.5px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton edge="start" size="small">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <ButtonGroup
              variant="contained"
              ref={anchorRef}
              sx={{
                borderRadius: "4px",
                overflow: "hidden",
                boxShadow: "none",
                "& .MuiButton-root": {
                  textTransform: "capitalize",
                  fontWeight: "400",
                  whiteSpace: "nowrap",
                },
              }}
              size="small"
            >
              <Button
                variant="outlined"
                endIcon={<ArrowDropDownIcon />}
                aria-haspopup="menu"
                onClick={handleExportToggle}
                size="medium"
              >
                Export
              </Button>
            </ButtonGroup>
            <Popper
              open={openExport}
              anchorEl={anchorRef.current}
              role={undefined}
              transition
              disablePortal
              placement="bottom-end"
              sx={{
                zIndex: 10,
              }}
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === "bottom-end" ? "top right" : "bottom right",
                  }}
                >
                  <Paper
                    sx={{
                      marginTop: "8px",
                      minWidth: "160px",
                      "& .MuiList-root": {
                        padding: 0,
                      },
                      "& .MuiMenuItem-root": {
                        padding: "8px 16px",
                        borderRadius: "4px",
                      },
                    }}
                  >
                    <ClickAwayListener onClickAway={handleExportMenuClose}>
                      <MenuList id="export-menu" autoFocusItem>
                        <MenuItem
                          selected={selectedExport === 0}
                          onClick={() => handleExportMenuItemClick(0, "csv")}
                        >
                          Export as CSV
                        </MenuItem>
                        <MenuItem
                          selected={selectedExport === 1}
                          onClick={() => handleExportMenuItemClick(1, "json")}
                        >
                          Export as JSON
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
        }}
      >
        <Paper variant="outlined" sx={{ height: "100%", width: "100%" }}>
          <DataGrid
            // rows={filteredRows.filter((row) => {
            //   const q = row.question?.toLowerCase() || "";
            //   const r = row.response?.toLowerCase() || "";

            //   // Hide internal AI prompt conversations
            //   if (q.startsWith("based on the following content")) return false;
            //   if (q.includes("generate 4 short") && q.includes("questions")) return false;

            //   // Hide system metadata from backend
            //   const meta = row.conversation_metadata || {};
            //   if (meta.is_internal) return false;
            //   if (meta.is_system_generated) return false;
            //   if (meta.internal_type?.includes("prompt")) return false;

            //   return true;
            // })}
            getRowId={(row) => row.created_at}
            rows={filteredRows}
            columns={columns}
            rowCount={totalMessages}
            initialState={{ pagination: { paginationModel: pagination } }}
            pageSizeOptions={[10]}
            onPaginationModelChange={handlePaginationMetaChange} // Handle pagination meta change
            paginationMode="server"
            disableColumnResize
            disableRowSelectionOnClick
            sx={{
              border: 0,
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default ConversationHistory;
